import { Box, Container } from "@material-ui/core";
import { RouteComponentProps } from "react-router";
import { useEffect, useMemo } from "react";
import { Loader } from "swyft-react-core";
import { useDispatch, useSelector } from "react-redux";

import { useStyles } from "./checkoutSuccessPage.styles";
import { Header } from "./components/Header";
import { HeroSection } from "./components/HeroSection";
import { boilerSliderImages, heroSliderImages } from "./checkoutSuccessPage.constants";
import { TopBox } from "../../components/TopBox";
import { QuoteDetails } from "./components/QuoteDetails";
import { RootState } from "../../app/rootReducer";
import { EEventType, eventAnalytic } from "../../app/analytics";
import { fetchProductSubscriptions } from "../../store/ProductSubscriptionSlice";
import { fetchQuote, syncQuote } from "../../store/QuoteSlice";
import { BotBox } from "../../components/BotBox";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { AssessmentComment } from "./components/AssessmentComment";
import { OrderSupport } from "./components/orderSupport/orderSupport";
import { FinancalSupports } from "./components/FinancalSupports/FinancalSupports";
import Slider, { Settings } from "react-slick";

type Props = RouteComponentProps<{ id: string }>;

export const CheckoutSuccessPage = ({ match }: Props) => {
  const classes = useStyles();
  const { isMoreThanMd, isMoreThanSm } = useBreakpoint();
  const dispatch = useDispatch();
  const { quote } = useSelector((state: RootState) => state.quote);

  eventAnalytic.useEffect(EEventType.StripeGateWaySuccess);
  const quoteId = match.params.id;

  useEffect(() => {
    dispatch(fetchProductSubscriptions());
    dispatch(fetchQuote(quoteId, true));
    dispatch(syncQuote(quoteId, true));
  }, [quoteId, dispatch]);

  eventAnalytic.useQuoteEffect(quote);

  const sliderSettings: Settings = {
    dots: false,
    infinite: true,
    arrows: false,
    pauseOnHover: false,
    pauseOnDotsHover: false,
    fade: true,
    speed: 3000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000
  };

  const sliderData = useMemo(() => {
    return quote?.type?.includes("Solar") ? heroSliderImages : boilerSliderImages;
  }, [quote]);

  if (!quote) return <Loader />;
  return (
    <Box bgcolor={"#fff"} className={classes.root}>
      <Box position={"relative"} display={"grid"} height={isMoreThanSm ? "1164px" : "655px"} overflow={"hidden"}>
        <Box className={classes.gradient} zIndex={1} height={"100%"}>
          <Box position={"absolute"} height={"100%"} width={"100%"}>
            <Container maxWidth={"lg"}>
              <Header />
            </Container>
          </Box>
          <Box height={"100%"}>
            <HeroSection quote={quote} />
          </Box>
          <Box marginTop={isMoreThanMd ? "-145px" : "-70px"}>
            <TopBox bgColor="#f7f7f7" outsideBgColor="transparent" boxHeight={isMoreThanMd ? "145px" : "71px"} />
          </Box>
        </Box>
        <Box
          position={"absolute"}
          top="0"
          left={0}
          width={"100%"}
          height={"100%"}
          className={`${classes.slider} headerFade`}
        >
          <Slider {...sliderSettings}>
            {sliderData.map((item, idx) => (
              <img loading="lazy" src={item} className="object-contain" alt={`Checkout Success ${idx}`} />
            ))}
          </Slider>
        </Box>
      </Box>
      <Box bgcolor="#f7f7f7">
        <Container maxWidth={"lg"}>
          <Box
            bgcolor={isMoreThanMd ? "#fff" : "transparent"}
            borderRadius={"10px"}
            padding={isMoreThanMd ? "53px 100px" : isMoreThanSm ? "53px" : "0"}
          >
            <QuoteDetails quote={quote} />
          </Box>
        </Container>
        <BotBox bgColor="#f7f7f7" outsideBgColor="#fff" boxHeight={isMoreThanMd ? "145px" : "71px"} />
      </Box>
      <Box bgcolor="#fff" paddingTop={isMoreThanSm ? "200px" : "80px"} paddingBottom={isMoreThanSm ? "150px" : "40px"}>
        <OrderSupport />
      </Box>
      <Box bgcolor="#f7f7f7">
        <TopBox bgColor="#f7f7f7" outsideBgColor="#fff" boxHeight={isMoreThanMd ? "145px" : "71px"} />
        <AssessmentComment quote={quote} />
        <BotBox bgColor="#f7f7f7" outsideBgColor="#fff" boxHeight={isMoreThanMd ? "145px" : "71px"} />
      </Box>
      <Box bgcolor="#fff">
        <FinancalSupports quote={quote} />
      </Box>
    </Box>
  );
};
